<template>
  <footer
    class="mt-auto border-t border-gray-200 bg-gradient-to-b from-white to-gray-50/50 transition-colors duration-300 dark:border-slate-700 dark:from-slate-900 dark:to-slate-900/80"
  >
    <div :class="['max-w-6xl px-4 py-8 md:px-8 md:py-10', centered ? 'mx-auto' : '']">
      <div class="grid gap-8 lg:grid-cols-[1.5fr_1fr] lg:gap-16">
        <div class="space-y-5">
          <div class="flex items-center">
            <IconLogo compact class="opacity-90" />
          </div>

          <div class="space-y-4">
            <h2
              v-translate="'footer.contactHeading'"
              class="text-xl font-bold text-gray-900 dark:text-white md:text-2xl"
            ></h2>

            <div class="space-y-2">
              <p
                v-translate="'footer.phone'"
                class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-500"
              ></p>
              <a
                href="tel:+4747700700"
                class="inline-flex items-center text-2xl font-bold text-gray-900 transition-all hover:scale-105 hover:text-emerald-600 dark:text-white dark:hover:text-emerald-400 md:text-3xl"
              >
                +47 477 00 700
              </a>
            </div>
          </div>

          <div>
            <RouterLink
              :to="contactFormRoute"
              class="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-900/20 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-900/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 dark:from-emerald-500 dark:to-emerald-400 dark:shadow-emerald-500/20 dark:hover:shadow-emerald-500/30"
            >
              <span v-translate="'footer.contactFormButton'"></span>
              <IconArrowRight class="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </RouterLink>
          </div>
        </div>

        <div class="space-y-4">
          <h3 v-translate="'footer.linksHeading'" class="text-base font-bold text-gray-900 dark:text-white"></h3>
          <nav class="space-y-2">
            <component
              :is="link.type === 'route' ? 'RouterLink' : 'a'"
              v-for="link in footerLinks"
              :key="link.key"
              v-bind="resolveLinkProps(link)"
              class="group flex items-center justify-between rounded-xl border border-gray-200/60 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 hover:shadow-md dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-300 dark:hover:border-emerald-500/50 dark:hover:bg-slate-800 dark:hover:text-emerald-300"
            >
              <span v-translate="link.key"></span>
              <IconArrowRight
                class="h-3.5 w-3.5 text-gray-400 transition-all group-hover:translate-x-0.5 group-hover:text-emerald-500 dark:text-slate-500 dark:group-hover:text-emerald-400"
              />
            </component>
          </nav>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from "vue";

import IconArrowRight from "@core/icons/IconArrowRight.vue";
import IconLogo from "@core/icons/IconLogo.vue";
import type { RouteLocationRaw } from "vue-router";

const props = defineProps<{ centered?: boolean }>();
const centered = computed(() => props.centered ?? false);

const contactFormRoute = computed<RouteLocationRaw>(() => ({ name: "customer-service" }));

type FooterLink =
  { key: string; type: "route"; to: { name: string } } | { key: string; type: "external"; href: string };

const footerLinks = computed<FooterLink[]>(() => [
  {
    key: "footer.links.privacyPolicy",
    type: "route",
    to: { name: "privacypolicy" }
  },
  {
    key: "footer.links.termsConditions",
    type: "route",
    to: { name: "terms" }
  },
  {
    key: "footer.links.transparency",
    type: "external",
    href: "/download/openness"
  },
  {
    key: "footer.links.dataProcessors",
    type: "route",
    to: { name: "dataprocessors" }
  },
  {
    key: "footer.links.press",
    type: "external",
    href: "mailto:presse@finter.no"
  }
]);

const resolveLinkProps = (link: (typeof footerLinks.value)[number]) => {
  if (link.type === "route") {
    return { to: link.to };
  }
  return { href: link.href, target: link.href.startsWith("mailto:") ? undefined : "_blank", rel: "noreferrer" };
};
</script>
